import { useState } from 'react';
import { useModal } from '../../../hooks/useModal';
import { useChangePlanMutation, useLazyGetUpcomingInvoiceQuery } from '../api/subscriptionApi';
import { Button } from '../../../components/Button';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { SubscriptionType } from '../api/types';
import { formatNumberToCurrency } from '../../../utils';

type UseSwitchPlanModalProps = {
  customerId: string;
};

export const useSwitchPlanModal = ({ customerId }: UseSwitchPlanModalProps) => {
  const [changePlan, { isLoading: loadingChangePlan }] = useChangePlanMutation();
  const [fetchUpcomingInvoice, { data: upcomingInvoice, isFetching: loadingPreview }] =
    useLazyGetUpcomingInvoiceQuery();
  const [isOpen, setIsOpen] = useState(false);
  const [newPlan, setNewPlan] = useState<SubscriptionType | null>(null);

  const onCloseModal = () => {
    setNewPlan(null);
    setIsOpen(false);
  };

  const { ModalComponent } = useModal({
    isOpen,
    onClose: onCloseModal,
    title: 'Switch plan confirmation'
  });

  const onSwitchPlan = async () => {
    if (!newPlan) return;
    try {
      await changePlan({ customerId, newPlanId: newPlan.id }).unwrap();
      toast.success('Plan switched successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to switch plan');
    }
    onCloseModal();
  };

  const onButtonPress = (newPlan: SubscriptionType) => {
    setNewPlan(newPlan);
    setIsOpen(true);
    // Preview what the next invoice will look like on the new plan
    fetchUpcomingInvoice({ customerId, newPlanId: newPlan.id });
  };

  const ConfirmationModal = (
    <ModalComponent>
      <ContentWrapper>
        <h4>Are you sure you want to switch to the plan?</h4>

        {newPlan && (
          <PlanDetails>
            <p>New plan:</p>
            <p>{newPlan.product_name}</p>
            <p>
              Price: {formatNumberToCurrency(newPlan.unit_amount / 100, newPlan.currency)} /{' '}
              {newPlan.recurring?.interval}
            </p>
          </PlanDetails>
        )}
        {loadingPreview ? (
          <Caption>Calculating your next charge…</Caption>
        ) : upcomingInvoice?.amount_due_pence != null ? (
          <ChargePreview>
            Estimated next charge:{' '}
            <strong>
              {formatNumberToCurrency(
                upcomingInvoice.amount_due_pence / 100,
                upcomingInvoice.currency ?? 'gbp'
              )}
            </strong>
            {upcomingInvoice.next_payment_date ? ` on ${upcomingInvoice.next_payment_date}` : null}
            {' '}(includes pro-rata adjustment)
          </ChargePreview>
        ) : null}
        <Caption>
          By switching plans, you will be charged immediately for the new plan amount on a pro-rata
          basis.
        </Caption>
      </ContentWrapper>
      <ButtonWrapper>
        <Button onClick={onSwitchPlan} style={{ width: '50%' }} isLoading={loadingChangePlan}>
          Yes
        </Button>
        <Button variant="danger" onClick={onCloseModal} style={{ width: '50%' }}>
          Cancel
        </Button>
      </ButtonWrapper>
    </ModalComponent>
  );

  return { onSwitchPlan, ConfirmationModal, onButtonPress };
};

const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.primary200};
  background-color: ${({ theme }) => theme.colors.primary200};
  padding: 1rem;
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.small};

  & > :first-child {
    font-weight: bold;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  flex: 1;
  & > h4 {
    margin: 0;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const Caption = styled.p`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.gray900};
`;

const ChargePreview = styled.p`
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.black};
  background: ${({ theme }) => theme.colors.primary100};
  border: 1px solid ${({ theme }) => theme.colors.primary200};
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
`;
